import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';

import InboxRouter from "./InboxRouter";
import PatternsRouter from "./PatternsRouter";
import TrendsRouter from "./TrendsRouter";
import ReportsRouter from "./ReportsRouter";
import ProtectRouter from "./ProtectRouter";
import SettingsRouter from "./SettingsRouter";
import UnauthenticatedRouter from "./UnauthenticatedRouter";

import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import NoMatch from "../components/NoMatch";
import PrivacyPolicy from "../components/PrivacyPolicy";
import Logout from "../containers/Logout";

function AppRouter(props) {
	const authenticatedUser = useContext(AuthorizedUserContext);

	let {updateHeader, resetHeader, showSnackbar,
		loginCallback, loadUserFailed, logoutCallback, unauthenticated,
		updateSnackbarMessage, evictUser,
		history, previousLocation, setBannerClassName,
		accessDeniedMsg, noAccessMsg,
		somethingWrongMsg, pageNotFoundMsg, setFullScreenClassName,
		banner, bannerHeight, showBanner, updateUser, csm, hasNudgeBanner, removeBgMask} = props;

	return (
		authenticatedUser ?
			<Switch>
				{/* Please note:
				  * - use component prop when each visit should trigger mount/unmount lifecycle; render
				  * doesn't, so is faster
				  * - all paths are relative to basename, /spa, specified in BrowserRouter in Index.js
				  */}

				<Route
					path={InboxRouter.getRoutes()}
					render={renderProps =>
						<InboxRouter
							{...renderProps}
							updateUser={updateUser}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							hasNudgeBanner={hasNudgeBanner}
							setBannerClassName={setBannerClassName} />
					} />

				<Route
					path={PatternsRouter.getRoutes()}
					render={renderProps =>
						<PatternsRouter
							{...renderProps}
							history={history}
							previousLocation={previousLocation}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							hasNudgeBanner={hasNudgeBanner}
							setBannerClassName={setBannerClassName}
							setFullScreenClassName={setFullScreenClassName} />
					} />

				<Route
					path={TrendsRouter.getRoutes()}
					render={renderProps =>
						<TrendsRouter
							{...renderProps}
							history={history}
							previousLocation={previousLocation}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							hasNudgeBanner={hasNudgeBanner}
							setBannerClassName={setBannerClassName}
							setFullScreenClassName={setFullScreenClassName} />
					} />

				<Route
					path={ReportsRouter.getRoutes()}
					render={renderProps =>
						<ReportsRouter
							{...renderProps}
							csm={csm}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							hasNudgeBanner={hasNudgeBanner}
							setBannerClassName={setBannerClassName}
							setFullScreenClassName={setFullScreenClassName} />
					} />

				<Route
					path={ProtectRouter.getRoutes()}
					render={renderProps =>
						<ProtectRouter
							{...renderProps}
							history={history}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							showBanner={showBanner}
							banner={banner}
							bannerHeight={bannerHeight}
							hasNudgeBanner={hasNudgeBanner}
							setBannerClassName={setBannerClassName}
							setFullScreenClassName={setFullScreenClassName} />
					} />

				<Route
					path={SettingsRouter.getRoutes()}
					render={renderProps =>
						<SettingsRouter
							{...renderProps}
							evictUser={evictUser}
							updateUser={updateUser}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							banner={banner}
							bannerHeight={bannerHeight}
							showBanner={showBanner}
							hasNudgeBanner={hasNudgeBanner}
							setBannerClassName={setBannerClassName}
							setFullScreenClassName={setFullScreenClassName} />
					} />

				<Route
					path="/no-access"
					render={renderProps =>
						<NoMatch
							{...renderProps}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							headline={accessDeniedMsg}
							subheading={noAccessMsg} />
					} />

				<Route
					path="/privacy"
					render={renderProps =>
						<PrivacyPolicy {...renderProps}
							updateHeader={updateHeader}
							resetHeader={resetHeader} />
					} />

				<Route
					path="/logout"
					render={renderProps =>
						<Logout
							{...renderProps}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							logoutCallback={logoutCallback} />
					} />

				{/*
					Going to /login when already authenticated causes a "blank" page
					This fixes that
				*/}
				<Redirect
					from="/login"
					to="/inbox" />

				{/* Can't redirect root to /signals like legacy because it causes problems with offline
					caching. Not a problem per se, especially if we ever show different views on the front
					page based on config or role. */}
				{/*<Route exact path="/" render={renderProps => (
					<Redirect to={{
						pathname: "/inbox",
						state: { from: props.location }
					}} />
				)}/>*/}
				{/*<Route
					exact
					path="/"
					render={renderProps =>
						<Activities
							{...renderProps}
							previousLocation={previousLocation}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							setBannerClassName={setBannerClassName}
							category={Constants.ACTIVITY.CATEGORIES.ALERTS.ID}	/>
					} />*/}
				{/* / -> /inbox */}
				<Redirect
					exact
					from="/"
					to={AuthorizedUser.hasInbox(authenticatedUser) ? "/inbox" :
						AuthorizedUser.hasDiscussions(authenticatedUser) ? "/trends" : "/"} />

				{/* No match - 404 */}
				<Route
					render={renderProps =>
						<NoMatch
							{...renderProps}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							headline={somethingWrongMsg}
							subheading={pageNotFoundMsg} />
					} />
			</Switch>
			:
			<Switch>
				<Route
					path={UnauthenticatedRouter.getRoutes()}
					render={renderProps =>
						<UnauthenticatedRouter
							{...renderProps}
							removeBgMask={removeBgMask}
							updateHeader={updateHeader}
							resetHeader={resetHeader}
							showSnackbar={showSnackbar}
							evictUser={evictUser}
							updateSnackbarMessage={updateSnackbarMessage}
							loginCallback={loginCallback}
							loadUserFailed={loadUserFailed}
							unauthenticated={unauthenticated} />
					} />
			</Switch>
		: null
	);
}

AppRouter.propTypes = {
	history: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
	updateHeader: PropTypes.func.isRequired,
	resetHeader: PropTypes.func.isRequired,
	previousLocation: PropTypes.object,
	showSnackbar: PropTypes.func.isRequired,
	loginCallback: PropTypes.func.isRequired,
	loadUserFailed: PropTypes.func.isRequired,
	logoutCallback: PropTypes.func.isRequired,
	unauthenticated: PropTypes.bool,
	updateSnackbarMessage: PropTypes.func.isRequired,
	evictUser: PropTypes.func.isRequired,
	setBannerClassName: PropTypes.func.isRequired,
	accessDeniedMsg: PropTypes.string.isRequired,
	noAccessMsg: PropTypes.string.isRequired,
	somethingWrongMsg: PropTypes.string.isRequired,
	pageNotFoundMsg: PropTypes.string.isRequired,
	banner: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
	bannerHeight: PropTypes.number,
	showBanner: PropTypes.func.isRequired,
	updateUser: PropTypes.func.isRequired,
	hasNudgeBanner: PropTypes.bool,
	removeBgMask: PropTypes.func.isRequired,
	setFullScreenClassName: PropTypes.func
};

export default AppRouter;